import { LocalDateTime } from "@js-joda/core";
import React from "react";
import {
  AddressComponents,
  PatientTaskBroadcastDetails,
  PatientTaskInstance,
} from "../../messages";
import { fmap } from "../../utils/fmap";
import { List } from "../../utils/List";
import { getScheduleFromTime, scheduleOptions } from "../../utils/taskUtils";
import { isDefined } from "../Generic/IsDefined";

export default function useScheduledVisitsAvailableFilters(params: {
  patientTasks: List<PatientTaskInstance>;
  patientTaskBroadcasts: List<PatientTaskBroadcastDetails>;
  visitPublicDetails: List<{
    location: AddressComponents;
    startTimeLocal: LocalDateTime;
  }>;
}) {
  const boroughs = React.useMemo(
    () => [
      ...new Set(
        params.patientTasks
          .map((task) => task.taskDetails.patientAddressComponent?.sublocalityLevel1)
          .concat(
            params.patientTaskBroadcasts.map(
              (task) => task.patientAddressComponents?.sublocalityLevel1
            )
          )
          .concat(params.visitPublicDetails.map((visit) => visit.location.sublocalityLevel1))
          .toArray()
          .filter(isDefined)
      ),
    ],
    [params.patientTaskBroadcasts, params.patientTasks, params.visitPublicDetails]
  );

  const visitTypes = React.useMemo(
    () => [
      ...new Set(
        params.patientTasks
          .map((task) => task.taskDetails.title)
          .concat(params.patientTaskBroadcasts.map((task) => task.title))
          .toArray()
          .concat(params.visitPublicDetails.length() > 0 ? ["HHA/PCA Visit"] : [])
      ),
    ],
    [params.patientTaskBroadcasts, params.patientTasks, params.visitPublicDetails]
  );

  const patientSchedule = React.useMemo(
    () =>
      [
        ...new Set(
          params.patientTasks
            .map((task) => fmap(task.scheduleDateTime, LocalDateTime.ofInstant))
            .concat(params.visitPublicDetails.map((visit) => visit.startTimeLocal))
            .toArray()
            .filter(isDefined)
            .map((dateTime) => getScheduleFromTime(dateTime.toLocalTime()))
        ),
      ].sort((a, b) => scheduleOptions.indexOf(a) - scheduleOptions.indexOf(b)),
    [params.patientTasks, params.visitPublicDetails]
  );

  return { boroughs, visitTypes, patientSchedule };
}
